// Generated from scripts/product-form.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { MORPH_OPTIONS, morph } from '@theme/morph';
import { cartPerformance } from '@theme/performance';
import { preloadImage, prefersReducedMotion } from '@theme/utilities';
import { resolveVariantId } from '@theme/variant-resolution';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents, } from '@shopify/events';
/** Names the add in the cart events, so a listener can tell the product form from the cart's own steppers. */
const SOURCE = 'product-form-component';
/** How long a button keeps its added state before it returns to idle. The bundle block's button shares it. */
export const ADDED_DURATION = 1400;
/** A request that has not answered by then is treated as lost: the add may or may not have happened. */
const ADD_TIMEOUT = 15000;
/** An add that uploads a file (a line item property) needs the time a phone connection takes to send it. */
const UPLOAD_TIMEOUT = 60000;
const READ_TIMEOUT = 8000;
/** How long the polite live region keeps a message, so a screen reader never meets a stale sentence. */
export const ANNOUNCEMENT_DURATION = 6000;
/** How long a transient message (the one a product card shows over its media) stays before it clears itself. */
const TRANSIENT_MESSAGE_DURATION = 6000;
/** The selling plan of a cart line as an add names it, or undefined when the line has none. */
export function sellingPlanOf(line) {
    const id = line.selling_plan_allocation?.selling_plan.id;
    return id === undefined ? undefined : String(id);
}
/** The line item properties of a cart line as strings, or undefined when it has none. */
export function propertiesOf(line) {
    const entries = Object.entries(line.properties ?? {}).filter((entry) => typeof entry[1] === 'string');
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}
/**
 * The add that recreates a cart line: its variant (or `variantId` when the line is being swapped for another), the
 * quantity, the line item properties and the selling plan it holds in the cart now. Read the line from the cart when
 * it is needed, not from the page, which can be a render behind.
 */
export function addLineFor(line, variantId = line.variant_id) {
    const properties = propertiesOf(line);
    const sellingPlan = sellingPlanOf(line);
    return { id: String(variantId), quantity: line.quantity, ...(properties ? { properties } : {}), ...(sellingPlan ? { sellingPlan } : {}) };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function isCartLine(value) {
    return (isRecord(value) &&
        typeof value.key === 'string' &&
        typeof value.variant_id === 'number' &&
        typeof value.quantity === 'number' &&
        (value.properties === null || isRecord(value.properties)));
}
export function isAjaxCart(value) {
    return isRecord(value) && typeof value.item_count === 'number' && Array.isArray(value.items) && value.items.every(isCartLine);
}
function isSections(value) {
    return isRecord(value) && Object.values(value).every((entry) => typeof entry === 'string' || entry === null);
}
function isFieldErrors(value) {
    return (isRecord(value) &&
        Object.values(value).every((entry) => typeof entry === 'string' || (Array.isArray(entry) && entry.every((item) => typeof item === 'string'))));
}
function refusalOf(body) {
    if (!isRecord(body))
        return { message: '', description: '', errors: undefined };
    return {
        message: typeof body.message === 'string' ? body.message : '',
        description: typeof body.description === 'string' ? body.description : '',
        errors: isFieldErrors(body.errors) && Object.keys(body.errors).length > 0 ? body.errors : undefined,
    };
}
function isLinesUpdate(event) {
    return 'promise' in event && event.promise instanceof Promise;
}
/** The number of items the cart held when the page last learned it; null until something tells. */
let knownItemCount = null;
/**
 * Tells the owner the item count the page was rendered with, when it does not know the count yet. A surface that
 * adds without a product form on the page (the bundle block can be placed without buy buttons) calls it when it
 * connects, so a lost response can still be settled by comparing counts. Once the page has learned a count from a
 * cart change, or from a form, this does nothing: the ledger is more current than a server render.
 */
export function rememberCartCount(count) {
    if (knownItemCount === null && Number.isInteger(count) && count >= 0)
        knownItemCount = count;
}
/** An abort signal that fires after `milliseconds` or with `parent`, and a way to stop the clock. */
export function withTimeout(milliseconds, parent) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(new DOMException('The request timed out.', 'TimeoutError')), milliseconds);
    const forward = () => controller.abort(parent?.reason);
    if (parent?.aborted)
        forward();
    else
        parent?.addEventListener('abort', forward, { once: true });
    return {
        signal: controller.signal,
        stop: () => {
            window.clearTimeout(timer);
            parent?.removeEventListener('abort', forward);
        },
    };
}
/** Reads the cart. Undefined when the read fails, so the caller decides what an unknown cart means. */
export async function readCart(signal) {
    const timeout = withTimeout(READ_TIMEOUT, signal);
    try {
        const response = await fetch(`${Theme.routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            signal: timeout.signal,
        });
        if (!response.ok)
            return undefined;
        const data = await response.json();
        return isAjaxCart(data) ? data : undefined;
    }
    catch {
        return undefined;
    }
    finally {
        timeout.stop();
    }
}
/** The sections of the cart on this page: the drawer and the cart page re-render from the answer of a cart write. */
export function cartSectionIds() {
    const ids = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
        if (component.dataset.sectionId)
            ids.add(component.dataset.sectionId);
    }
    return Array.from(ids).join(',');
}
/** The request an add makes: the form's own entries as multipart, or JSON items. */
function addRequest(options) {
    const sections = cartSectionIds();
    if (options.form) {
        const [line] = options.lines;
        const data = new FormData(options.form);
        data.set('id', line.id);
        data.set('quantity', String(line.quantity));
        if (options.lines.length > 1) {
            // Keep file objects and third-party fields while moving the primary purchase into the multi-item form.
            const entries = Array.from(data.entries());
            for (const [key] of entries)
                if (key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties['))
                    data.delete(key);
            for (const [key, value] of entries) {
                if (key === 'id' || key === 'quantity' || key === 'selling_plan')
                    data.append(`items[0][${key}]`, value);
                else if (key.startsWith('properties['))
                    data.append(`items[0][properties]${key.slice('properties'.length)}`, value);
            }
            for (const [index, child] of options.lines.slice(1).entries()) {
                const prefix = `items[${index + 1}]`;
                data.set(`${prefix}[id]`, child.id);
                data.set(`${prefix}[quantity]`, String(child.quantity));
                if (child.parentId)
                    data.set(`${prefix}[parent_id]`, child.parentId);
                if (child.sellingPlan)
                    data.set(`${prefix}[selling_plan]`, child.sellingPlan);
                for (const [key, value] of Object.entries(child.properties ?? {}))
                    data.set(`${prefix}[properties][${key}]`, value);
            }
        }
        if (sections)
            data.set('sections', sections);
        // No Content-Type: the browser writes the multipart boundary itself.
        return { body: data, headers: { Accept: 'application/json' } };
    }
    return {
        body: JSON.stringify({
            items: options.lines.map(({ id, quantity, properties, sellingPlan, parentId }) => ({
                id: Number(id),
                quantity,
                ...(properties && Object.keys(properties).length > 0 ? { properties } : {}),
                ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
                ...(parentId ? { parent_id: Number(parentId) } : {}),
            })),
            ...(sections ? { sections } : {}),
        }),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
}
/** Whether a form body carries a file, which takes longer to send than text. */
function hasFile(body) {
    return body instanceof FormData && Array.from(body.values()).some((value) => value instanceof File && value.size > 0);
}
/**
 * Posts the add. A response is `answered` when it carries a status the cart decided (a success or a refusal in the
 * 4xx range); a network failure, a timeout, a server error and a body that is not JSON are all `lost`, because the
 * cart may have applied the add before the answer went missing.
 */
async function postAdd(options) {
    const { body, headers } = addRequest(options);
    const timeout = withTimeout(hasFile(body) ? UPLOAD_TIMEOUT : ADD_TIMEOUT);
    try {
        const response = await fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers,
            credentials: 'same-origin',
            body,
            signal: timeout.signal,
        });
        if (response.status >= 500)
            return { kind: 'lost' };
        const parsed = await response.json();
        return { kind: 'answered', ok: response.ok, status: response.status, body: parsed };
    }
    catch {
        return { kind: 'lost' };
    }
    finally {
        timeout.stop();
    }
}
/**
 * Adds one or more lines to the cart and publishes the result. The `CartLinesUpdateEvent` is dispatched once, before
 * the request; its promise settles with the cart as Shopify reports it (with `didError` when the cart refused), or
 * rejects when nothing can be said about the cart.
 *
 * A lost response is settled by reading the cart. The item count before the add is compared with the count after
 * it: the add is atomic, so a count raised by exactly the added quantity means it happened (and the add is
 * reported as `added`), an unchanged count means it did not (`not-added`), and anything else, or an unknown count
 * before, cannot be told (`unknown`).
 */
export async function addToCart(options) {
    if (options.form) {
        let extraLines = [];
        const personalizer = options.form.querySelector('wontio-personalization');
        if (personalizer) {
            try {
                const { personalizationFor } = await import('./personalization.js');
                const configuration = personalizationFor(options.form, options.lines[0]);
                if (!configuration.ok)
                    return { status: 'rejected', httpStatus: 422, message: configuration.message, description: configuration.message, errors: undefined };
                extraLines = configuration.lines;
            }
            catch {
                const message = personalizer.dataset.errorText ?? '';
                return { status: 'rejected', httpStatus: 422, message, description: message, errors: undefined };
            }
        }
        if (!options.form.checkValidity()) {
            options.form.reportValidity();
            const invalid = options.form.querySelector(':invalid');
            return { status: 'rejected', httpStatus: 422, message: invalid?.validationMessage ?? '', description: invalid?.validationMessage ?? '', errors: undefined };
        }
        const lines = [options.lines[0], ...options.lines.slice(1), ...extraLines];
        options = { ...options, lines };
    }
    const { source, signal } = options;
    const lines = options.lines;
    const countBefore = knownItemCount;
    const quantityAdded = lines.reduce((sum, line) => sum + line.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();
    // A rejection nobody listens for is not an error of the page: the listeners that care attach their own handlers.
    deferred.promise.catch(() => undefined);
    source.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: options.context ?? 'product',
        lines: lines.map((line) => ({ merchandiseId: line.id, quantity: line.quantity })),
        promise: deferred.promise,
    }));
    const detailBase = { source: SOURCE, sourceId: options.sourceId, productId: options.productId };
    /** Tells the listeners about a cart that accepted the lines, from a fresh read of the cart. */
    const settleAdded = async (sections, recovered) => {
        options.onAccepted?.();
        const cart = (await readCart(signal)) ?? (await readCart(signal));
        if (cart) {
            knownItemCount = cart.item_count;
            deferred.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: { ...detailBase, items: cart.items, itemCount: cart.item_count, sections, didError: false },
            });
        }
        else {
            knownItemCount = null;
            deferred.reject(new Error('The cart could not be read after the add.'));
        }
        return { status: 'added', cart, sections, recovered };
    };
    const answer = await postAdd(options);
    if (answer.kind === 'answered' && answer.ok) {
        const sections = isRecord(answer.body) && isSections(answer.body.sections) ? answer.body.sections : undefined;
        return settleAdded(sections, false);
    }
    if (answer.kind === 'answered') {
        // The cart decided: this is a refusal, and the cart is unchanged. It is read anyway, so every quantity on the
        // page agrees with it (a stock limit often means the cart already holds what is left).
        const refusal = refusalOf(answer.body);
        source.dispatchEvent(new CartErrorEvent({
            error: refusal.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: refusal.description, errors: refusal.errors },
        }));
        const cart = await readCart(signal);
        if (cart) {
            knownItemCount = cart.item_count;
            deferred.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: { ...detailBase, items: cart.items, itemCount: cart.item_count, didError: true },
            });
        }
        else {
            deferred.reject(new Error(refusal.description || refusal.message || 'Add to cart failed'));
        }
        return { status: 'rejected', httpStatus: answer.status, ...refusal };
    }
    // The response is lost: read the cart before saying anything about the add.
    const cart = await readCart(signal);
    if (cart && countBefore !== null && cart.item_count === countBefore + quantityAdded) {
        return settleAdded(undefined, true);
    }
    source.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    deferred.reject(new Error('The add to cart request did not complete.'));
    if (cart)
        knownItemCount = cart.item_count;
    return cart && countBefore !== null && cart.item_count === countBefore ? { status: 'not-added' } : { status: 'unknown' };
}
// The item count the page knows follows every cart change, whoever made it. The listener belongs to the module and
// not to an element, because the owner can run when no product form is on the page.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (!isLinesUpdate(event))
        return;
    event.promise
        ?.then(({ cart }) => {
        if (cart)
            knownItemCount = cart.totalQuantity;
    })
        .catch(() => undefined);
});
// A page restored from the back/forward cache shows a cart that may have changed since: the count is unknown again.
window.addEventListener('pageshow', (event) => {
    if (event.persisted)
        knownItemCount = null;
});
/**
 * The container of an add to cart button. The form tells it which phase to show; the phase is a set of attributes on
 * the button (`aria-busy` while adding, `data-added` once added) that the stylesheet turns into the spinner and the
 * check. The label keeps its place in the layout in every phase, so the button never changes its width.
 *
 * Contract kept from the inherited module: the `add-to-cart-component` element, the `addToCartButton` ref and the
 * `disable` and `enable` methods. `handleClick` and `animateAddToCart` are retired: the form owns the states now.
 */
export class AddToCartComponent extends Component {
    requiredRefs = ['addToCartButton'];
    #lifetime;
    #preloaded = '';
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        this.addEventListener('pointerenter', this.#preloadImage, { signal: lifetime.signal });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
    }
    get button() {
        const button = this.refs.addToCartButton;
        if (!(button instanceof HTMLButtonElement))
            throw new Error('Missing <button ref="addToCartButton" /> inside <add-to-cart-component />');
        return button;
    }
    disable() {
        this.button.disabled = true;
    }
    enable() {
        this.button.disabled = false;
    }
    /** Shows the phase on the button. */
    setPhase(phase) {
        const { button } = this;
        if (phase === 'adding')
            button.setAttribute('aria-busy', 'true');
        else
            button.removeAttribute('aria-busy');
        if (phase === 'added')
            button.setAttribute('data-added', 'true');
        else
            button.removeAttribute('data-added');
    }
    /**
     * Shows a selection no variant exists for: the label says so and the button cannot be pressed. The server renders
     * the first available variant for such a selection, so only the client knows.
     */
    markUnavailable() {
        const label = this.querySelector('[data-label]');
        if (label && this.dataset.unavailableLabel)
            label.textContent = this.dataset.unavailableLabel;
        // The price beside the label is the previous selection's; no variant means no price.
        const price = this.querySelector('[data-price]');
        if (price)
            price.hidden = true;
        this.disable();
    }
    /** Warms the cache with the variant image, so the motion that follows an add has its picture. */
    #preloadImage = () => {
        const image = this.dataset.productVariantMedia;
        if (!image || image === this.#preloaded)
            return;
        this.#preloaded = image;
        preloadImage(image);
    };
}
if (!customElements.get('add-to-cart-component')) {
    customElements.define('add-to-cart-component', AddToCartComponent);
}
/** Published by a product form (bubbling) whenever its phase or message changes. */
export class ProductFormStateEvent extends CustomEvent {
    static eventName = 'product-form:state';
    constructor(detail) {
        super(ProductFormStateEvent.eventName, { bubbles: true, detail });
    }
}
function isFormControl(element) {
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement;
}
function isProductSelect(event) {
    return 'promise' in event && event.promise instanceof Promise;
}
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
function isQuantitySelector(element) {
    return element instanceof HTMLElement && 'check' in element && typeof element.check === 'function';
}
function isAddToCart(element) {
    return element instanceof HTMLElement && 'setPhase' in element && typeof element.setPhase === 'function';
}
function elementOf(ref) {
    return ref instanceof HTMLElement ? ref : undefined;
}
/**
 * Writes a message into a slot and shows the slot; an empty message hides it. The words go into `[data-message]` when
 * the slot has one, so an icon beside them stays. A transient slot fades out, so it keeps its words while it does;
 * nothing refers to a hidden slot, and the next message replaces them.
 */
function setSlotMessage(slot, message) {
    const target = slot.querySelector('[data-message]') ?? slot;
    if (message !== '' || !slot.hasAttribute('data-transient'))
        target.textContent = message;
    slot.hidden = message === '';
}
/** The pieces of the page that depend on the variant: Shopify renders them again for each selection. */
const VARIANT_SLOTS = ['hints', 'cart-count', 'price-per-item', 'volume-pricing'];
/**
 * A product form: the one place a product page, a quick view, a featured product and a product card add to the
 * cart. It checks the quantity against the variant's quantity rule and every required line item property inline,
 * before any request; adds through `addToCart`; shows the add as idle, adding and added on its buttons, with the
 * message of a refusal next to the controls; and follows the variant picker, so the button, the quantity rule, the
 * volume pricing and the price per item always describe the variant that will be added.
 *
 * Rules the form keeps:
 * - A second submit while an add is in flight is ignored, so a double press makes one line.
 * - A submit while a variant change is in flight waits for the answer and adds the variant it resolved to. When the
 *   answer never came, the form adds only a variant it can read from the picker, never a stale one.
 * - A refusal (422) shows Shopify's own description inline and leaves the cart as it is. A lost response is
 *   settled by reading the cart; only then does the form offer the add again, and never by itself.
 *
 * Contract kept from the inherited module: the `product-form-component` and `add-to-cart-component` elements, the
 * `on:submit="/handleSubmit"` binding, the refs (`variantId`, `liveRegion`, `addToCartButtonContainer`,
 * `addToCartTextError`, `acceleratedCheckoutButtonContainer`, `quantitySelector`, `quantitySelectorWrapper`,
 * `quantityRules`, `quantityLabelCartCount`, `volumePricing`, `pricePerItem`, `productFormButtons`) and the cart
 * events with their payloads. `add` is new and is how other surfaces ask this form to add, and `dismissMessage` is
 * what the close control of a transient message (the one a product card draws over its media) calls.
 */
export class ProductFormComponent extends Component {
    requiredRefs = ['variantId', 'liveRegion'];
    #lifetime;
    #phase = 'idle';
    #error;
    /** True from the moment an add begins until it settles. */
    #adding = false;
    #addedTimer = 0;
    #announceTimer = 0;
    #messageTimer = 0;
    /** The newest variant change in flight; it never rejects. */
    #selection;
    /** True when the newest variant change ended without an answer, so the variant input may be out of date. */
    #selectionFailed = false;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        this.closest('.shopify-section, dialog, product-card')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
        document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
        document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
        document.addEventListener('input', this.#onFieldInput, { signal });
        document.addEventListener('change', this.#onFieldInput, { signal });
        // The page's own render is the first thing that knows the cart; the owner's ledger takes over from there.
        rememberCartCount(Number.parseInt(this.dataset.cartCount ?? '', 10));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        window.clearTimeout(this.#addedTimer);
        window.clearTimeout(this.#announceTimer);
        window.clearTimeout(this.#messageTimer);
    }
    /** The phase the buttons show now. */
    get phase() {
        return this.#phase;
    }
    get #form() {
        const form = this.querySelector('form');
        if (!form)
            throw new Error('Product form element missing');
        return form;
    }
    get #variantInput() {
        const input = this.refs.variantId;
        if (!(input instanceof HTMLInputElement))
            throw new Error('Missing <input ref="variantId" /> inside <product-form-component />');
        return input;
    }
    get #quantitySelector() {
        const selector = this.refs.quantitySelector;
        return isQuantitySelector(selector) ? selector : undefined;
    }
    get #buttons() {
        return Array.from(this.querySelectorAll('add-to-cart-component')).filter(isAddToCart);
    }
    /** Whether the variant on the form cannot be bought. */
    get #unavailable() {
        return this.#buttons.some((component) => component.button.disabled);
    }
    /** The form's submit handler, bound with `on:submit="/handleSubmit"`. */
    handleSubmit(event) {
        event.preventDefault();
        void this.add({ event });
    }
    /**
     * Adds the form's variant in the form's quantity. Other surfaces (the sticky bar) call it to add through this form,
     * so there is one set of checks and one add. Resolves when the add has settled; the outcome is on the page.
     */
    async add(options = {}) {
        if (this.#adding)
            return;
        this.#adding = true;
        const lifetime = this.#lifetime;
        try {
            await this.#run(options, lifetime?.signal);
        }
        finally {
            this.#adding = false;
            if (options.event)
                cartPerformance.measureFromEvent('add:user-action', options.event);
        }
    }
    async #run(options, signal) {
        window.clearTimeout(this.#addedTimer);
        this.#clearMessage();
        // A selection in flight decides the variant: the button shows the work while the answer arrives.
        if (this.#selection) {
            this.#setPhase('adding');
            while (this.#selection)
                await this.#selection;
            if (signal?.aborted)
                return;
        }
        const variantId = this.#resolveVariant();
        if (!variantId) {
            this.#setPhase('idle');
            // A selection that cannot be bought says so on the button; a selection the form could not read is an error.
            if (!this.#unavailable)
                this.#showMessage(this.dataset.errorText ?? '');
            return;
        }
        if (!this.#validate()) {
            this.#setPhase('idle');
            return;
        }
        this.#setPhase('adding');
        const outcome = await addToCart({
            form: this.#form,
            lines: [this.#line(variantId)],
            source: this,
            context: this.closest('dialog') ? 'dialog' : 'product',
            productId: this.dataset.productId,
            sourceId: this.id || undefined,
            onAccepted: () => this.#onAccepted(options),
            signal,
        });
        if (signal?.aborted)
            return;
        switch (outcome.status) {
            case 'added':
                break;
            case 'rejected':
                this.#setPhase('idle');
                this.#showMessage(outcome.description || outcome.message || this.dataset.errorText || '');
                break;
            case 'not-added':
                // The cart shows nothing was added, so pressing the button again is safe.
                this.#setPhase('idle');
                this.#showMessage(this.dataset.errorText ?? '');
                break;
            case 'unknown':
                // The cart cannot say; the shopper looks before asking again.
                this.#setPhase('idle');
                this.#showMessage(this.dataset.unconfirmedText ?? this.dataset.errorText ?? '');
                break;
            default: {
                const exhaustive = outcome;
                throw new Error(`Unhandled add outcome ${String(exhaustive)}`);
            }
        }
    }
    /** The cart accepted the add: the buttons show it, the picture flies, and screen readers hear it once. */
    #onAccepted(options) {
        this.#setPhase('added');
        this.#addedTimer = window.setTimeout(() => this.#setPhase('idle'), ADDED_DURATION);
        this.#announce(this.dataset.addedText ?? '');
        this.#flyToCart(options.origin);
    }
    /**
     * Starts the picture flying to the cart target, which the element resolves itself. The motion is a courtesy: it
     * is skipped under reduced motion, inside a dialog (the cart opens after the dialog closes) and without a picture.
     */
    #flyToCart(origin) {
        const [component] = this.#buttons;
        if (!component || component.dataset.addToCartAnimation !== 'true' || prefersReducedMotion() || this.closest('dialog'))
            return;
        const image = origin?.image ?? component.dataset.productVariantMedia;
        if (!image)
            return;
        const variant = origin ? 'fly-to-cart--sticky' : component.button.classList.contains('quick-add__button') ? 'fly-to-cart--quick' : 'fly-to-cart--main';
        const element = Object.assign(document.createElement('fly-to-cart'), {
            source: origin?.element ?? component.button,
            useSourceSize: Boolean(origin),
        });
        element.classList.add('tw:fly-to-cart-motion', variant, 'tw:[--start-opacity:0]');
        element.style.setProperty('--fly-to-cart-background-image', `url(${image})`);
        document.body.appendChild(element);
    }
    /* ----- the variant ----- */
    /**
     * The variant id to add: what the variant change resolved, or the one the picker names. A selection that never
     * got its answer leaves the hidden input out of date, so then only the picker's own variant is used.
     */
    #resolveVariant() {
        const intended = this.#pickerVariantId();
        return resolveVariantId({
            intendedVariantId: intended,
            hiddenInputValue: this.#selectionFailed ? null : this.#variantInput.value,
            available: this.#selectionFailed ? undefined : !this.#unavailable,
        });
    }
    /** The variant id the picker's checked option stands for. Only options that resolve to one variant carry it. */
    #pickerVariantId() {
        const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
        const pickers = Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? []);
        const { productId } = this.dataset;
        const picker = (productId ? pickers.find((candidate) => candidate instanceof HTMLElement && candidate.dataset.productId === productId) : undefined) ?? (pickers.length === 1 ? pickers[0] : undefined);
        const selected = picker?.querySelector('select option:checked, fieldset input:checked');
        return selected instanceof HTMLElement ? selected.dataset.variantId || null : null;
    }
    #onProductSelect = (event) => {
        if (!isProductSelect(event))
            return;
        // A product card's own selection is not this form's unless the form sits in that card.
        const card = event.target instanceof Element ? event.target.closest('product-card') : null;
        if (card && !card.contains(this))
            return;
        this.#clearMessage();
        const settled = event.promise
            .then((result) => {
            this.#selectionFailed = false;
            this.#applySelection(result);
        })
            .catch((error) => {
            // A newer selection aborted this one and has taken over.
            if (isAbort(error))
                return;
            this.#selectionFailed = true;
            console.warn('[product-form] The variant change failed:', error);
        })
            .finally(() => {
            if (this.#selection === tracked)
                this.#selection = undefined;
        });
        const tracked = settled;
        this.#selection = tracked;
    };
    /** Brings the form in line with the section Shopify rendered for the new selection. */
    #applySelection(result) {
        const detail = result.detail;
        if (!detail?.html)
            return;
        const { html, newProduct, productId, resource } = detail;
        if (newProduct)
            this.dataset.productId = newProduct.id;
        else if (productId && productId !== this.dataset.productId)
            return;
        const available = resource !== null && resource.available !== false;
        const next = this.#counterpartIn(html);
        // The hidden field carries the variant; the dynamic checkout buttons read it when it changes.
        const input = this.#variantInput;
        input.value = resource?.id === undefined ? '' : String(resource.id);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        const [component] = this.#buttons;
        const nextComponent = next?.querySelector('add-to-cart-component');
        if (component && nextComponent) {
            morph(component, nextComponent, { ...MORPH_OPTIONS, childrenOnly: false });
            if (resource === null)
                component.markUnavailable();
            else if (!available)
                component.disable();
            else
                component.enable();
        }
        const checkout = elementOf(this.refs.acceleratedCheckoutButtonContainer);
        if (checkout)
            checkout.hidden = !available;
        this.#followQuantityRule(next, available);
        for (const name of VARIANT_SLOTS) {
            const slot = this.querySelector(`[data-variant-slot="${name}"]`);
            const nextSlot = next?.querySelector(`[data-variant-slot="${name}"]`);
            if (slot && nextSlot)
                morph(slot, nextSlot, { ...MORPH_OPTIONS, childrenOnly: false });
        }
        if (newProduct) {
            const slot = this.querySelector('[data-variant-slot="personalization"]');
            const nextSlot = next?.querySelector('[data-variant-slot="personalization"]');
            if (slot && nextSlot) {
                morph(slot, nextSlot, { ...MORPH_OPTIONS, childrenOnly: false });
                if (slot.querySelector('wontio-personalization'))
                    void import('./personalization.js').catch(() => undefined);
            }
        }
        this.#renderPhase();
    }
    /** The form in the answer that stands for this one: the same section, else the first. */
    #counterpartIn(html) {
        const forms = Array.from(html.querySelectorAll('product-form-component')).filter((form) => form instanceof HTMLElement);
        return forms.find((form) => form.dataset.sectionId === this.dataset.sectionId) ?? forms[0];
    }
    /** Moves the quantity selector to the new variant's rule and the quantity its cart line holds. */
    #followQuantityRule(next, available) {
        const selector = this.#quantitySelector;
        const nextSelector = next?.querySelector('quantity-selector-component');
        const nextInput = nextSelector?.querySelector('input');
        if (!selector || !(nextSelector instanceof HTMLElement) || !nextInput)
            return;
        const input = selector.quantityInput;
        selector.dataset.variantId = nextSelector.dataset.variantId ?? '';
        for (const key of ['errorMin', 'errorMax', 'errorIncrement']) {
            const message = nextSelector.dataset[key];
            if (message === undefined)
                delete selector.dataset[key];
            else
                selector.dataset[key] = message;
        }
        input.dataset.min = nextInput.dataset.min ?? '1';
        input.setAttribute('data-cart-quantity', nextInput.getAttribute('data-cart-quantity') ?? '0');
        selector.updateConstraints(nextInput.min, nextInput.max || null, nextInput.step);
        selector.setDisabled(nextInput.disabled || !available);
        this.dataset.quantityDefault = nextInput.min || '1';
    }
    /* ----- the cart ----- */
    /** Keeps the quantity this form shows as already in the cart true after any cart change. */
    #onCartUpdate = (event) => {
        if (!isLinesUpdate(event))
            return;
        event.promise
            ?.then(({ detail }) => {
            if (detail?.items)
                this.#syncInCart(detail.items);
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[product-form] The cart update was not confirmed:', error);
        });
    };
    #syncInCart(items) {
        const variantId = this.#variantInput.value;
        if (!variantId)
            return;
        const quantity = items.filter((item) => String(item.variant_id) === variantId).reduce((sum, item) => sum + item.quantity, 0);
        this.#quantitySelector?.setCartQuantity(quantity);
        // The badge on the button carries the count; the hints below it carry only the variant's quantity rule.
        const inCart = elementOf(this.refs.quantityLabelCartCount);
        const count = inCart?.querySelector('[data-count]');
        if (inCart && count) {
            count.textContent = String(quantity);
            inCart.hidden = quantity === 0;
        }
    }
    /* ----- the lines ----- */
    /**
     * The variant and quantity the form adds. Everything else the form holds (the selling plan, the line item
     * properties including files, a field an app added) travels in the form's own entries, which the owner posts.
     */
    #line(variantId) {
        const quantity = Number(new FormData(this.#form).get('quantity')) || Number(this.dataset.quantityDefault) || 1;
        return { id: variantId, quantity };
    }
    /* ----- validation ----- */
    /**
     * Checks the quantity and every line item property before any request. Each failure is written next to its
     * control and the first one gets focus. Returns whether the form can be added.
     */
    #validate() {
        const problems = [];
        const selector = this.#quantitySelector;
        const check = selector?.check();
        if (selector && check && !check.ok) {
            this.#showQuantityMessage(selector, check);
            problems.push({ control: selector.quantityInput, message: this.#quantityText(selector, check), inline: true });
        }
        for (const element of Array.from(this.#form.elements)) {
            if (!isFormControl(element) || !element.name.startsWith('properties[') || !element.willValidate || element.validity.valid)
                continue;
            const message = element.validity.valueMissing ? this.dataset.requiredText || element.validationMessage : element.validationMessage;
            problems.push({ control: element, message, inline: this.#showFieldError(element, message) });
        }
        if (problems.length === 0)
            return true;
        problems.sort((a, b) => (a.control.compareDocumentPosition(b.control) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
        const [first] = problems;
        if (!first)
            return false;
        // A field without a message slot of its own (an app's required input) gets the browser's bubble, which also
        // takes focus; a slot's message is read from the field, which is described by it.
        if (first.inline)
            first.control.focus();
        else if (first.control instanceof HTMLInputElement || first.control instanceof HTMLTextAreaElement || first.control instanceof HTMLSelectElement)
            first.control.reportValidity();
        // A control that cannot take focus (a block a quick view hides) would leave the shopper with no sign at all, so
        // the first message also goes where the add's own messages go.
        if (document.activeElement !== first.control)
            this.#showMessage(first.message);
        else
            this.#announce(first.message);
        return false;
    }
    #quantityText(selector, check) {
        const text = { min: selector.dataset.errorMin, max: selector.dataset.errorMax, increment: selector.dataset.errorIncrement }[check.reason];
        return text ?? '';
    }
    /** Says in words, next to the stepper, why a quantity cannot be added or had to change. */
    #showQuantityMessage(selector, check) {
        const slot = this.querySelector('[data-quantity-message]');
        const text = this.#quantityText(selector, check);
        if (!slot || !text)
            return;
        setSlotMessage(slot, text);
        selector.quantityInput.setAttribute('aria-invalid', 'true');
    }
    #clearQuantityMessage() {
        const selector = this.#quantitySelector;
        const slot = this.querySelector('[data-quantity-message]');
        if (slot)
            setSlotMessage(slot, '');
        selector?.quantityInput.removeAttribute('aria-invalid');
    }
    /** A change of the quantity clears the message about it; a correction the selector made is explained. */
    #onQuantityUpdate = (event) => {
        if (!(event.target instanceof Node) || !this.contains(event.target))
            return;
        this.#clearQuantityMessage();
        const selector = this.#quantitySelector;
        const correction = selector?.takeCorrection();
        if (selector && correction && !correction.ok) {
            this.#showQuantityMessage(selector, correction);
            this.#announce(this.#quantityText(selector, correction));
        }
    };
    /** Writes a property's message in its field's slot and marks it invalid. Returns whether the field has a slot. */
    #showFieldError(control, message) {
        const slot = control.closest('[data-field]')?.querySelector('[data-field-error]');
        if (slot)
            setSlotMessage(slot, message);
        control.setAttribute('aria-invalid', 'true');
        return Boolean(slot);
    }
    /** A field the shopper edits stops being invalid as soon as it is valid again. */
    #onFieldInput = (event) => {
        const control = event.target;
        if (!(control instanceof Element) || !isFormControl(control) || control.form !== this.querySelector('form'))
            return;
        if (control.getAttribute('aria-invalid') !== 'true' || !control.validity.valid)
            return;
        control.removeAttribute('aria-invalid');
        const slot = control.closest('[data-field]')?.querySelector('[data-field-error]');
        if (slot)
            setSlotMessage(slot, '');
    };
    /* ----- what the shopper sees ----- */
    #setPhase(phase) {
        this.#phase = phase;
        this.#renderPhase();
    }
    /** Projects the phase and the message onto the buttons and tells the surfaces that mirror the form. */
    #renderPhase() {
        for (const component of this.#buttons)
            component.setPhase(this.#phase);
        this.dispatchEvent(new ProductFormStateEvent({ phase: this.#phase, error: this.#error }));
    }
    /**
     * Shows a message next to the controls, with the button described by it, and announces it once. A slot marked
     * `data-transient` (the message a product card draws over its media) clears itself after a few seconds.
     */
    #showMessage(message) {
        if (!message)
            return;
        this.#error = message;
        const slot = elementOf(this.refs.addToCartTextError);
        if (slot)
            setSlotMessage(slot, message);
        window.clearTimeout(this.#messageTimer);
        if (slot?.hasAttribute('data-transient')) {
            this.#messageTimer = window.setTimeout(() => this.#clearMessage(), TRANSIENT_MESSAGE_DURATION);
        }
        this.#announce(message);
        this.#renderPhase();
    }
    #clearMessage() {
        window.clearTimeout(this.#messageTimer);
        this.#clearQuantityMessage();
        if (this.#error === undefined)
            return;
        this.#error = undefined;
        const slot = elementOf(this.refs.addToCartTextError);
        if (slot) {
            // A message that closes under the shopper's focus (its close control) hands the focus back to the button.
            const focused = slot.contains(document.activeElement);
            setSlotMessage(slot, '');
            if (focused)
                this.#buttons[0]?.button.focus();
        }
        this.#renderPhase();
    }
    /** Clears the message next to the controls. The close control of the message over a product card's media is bound to it. */
    dismissMessage() {
        this.#clearMessage();
    }
    /** Speaks through the form's polite live region. */
    #announce(text) {
        if (!text)
            return;
        const region = this.refs.liveRegion;
        if (!(region instanceof HTMLElement))
            return;
        region.textContent = text;
        window.clearTimeout(this.#announceTimer);
        this.#announceTimer = window.setTimeout(() => {
            region.textContent = '';
        }, ANNOUNCEMENT_DURATION);
    }
}
if (!customElements.get('product-form-component')) {
    customElements.define('product-form-component', ProductFormComponent);
}
